import React from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './datepicker-theme.css';
import { Calendar, Clock } from 'lucide-react';

const ReactDatePicker = DatePicker as unknown as React.FC<any>;
const Icons = { Calendar, Clock } as any;

export interface DateTimePickerProps {
    value?: string;
    onChange?: (value: string) => void;
    label?: string;
    pickerType?: 'date-time' | 'date' | 'time';
    error?: string;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    minDate?: string;         // 'YYYY-MM-DD'
    maxDate?: string;         // 'YYYY-MM-DD'
    minTime?: string;         // 'HH:mm'
    maxTime?: string;         // 'HH:mm'
    required?: boolean;
    disabled?: boolean;
    size?: 'small' | 'medium' | 'large';
    variant?: 'default' | 'filled' | 'outlined';
    helperText?: string;
    id?: string;
    name?: string;
}

function cls(...a: Array<string | false | null | undefined>) {
    return a.filter(Boolean).join(' ');
}

/* --------------------------- parsing helpers --------------------------- */
function parseDateOnly(s?: string): Date | null {
    if (!s) return null;
    const d = new Date(s + 'T00:00');
    return Number.isNaN(d.getTime()) ? null : d;
}
function parseTimeOnly(s?: string): Date | null {
    if (!s) return null;
    const [hh, mm] = s.split(':').map(Number);
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
    const d = new Date();
    d.setHours(hh, mm, 0, 0);
    return d;
}
function parseDateTime(s?: string): Date | null {
    if (!s) return null;
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
}
function fmtDate(d: Date | null) {
    if (!d) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
function fmtTime(d: Date | null) {
    if (!d) return '';
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
}
function fmtDateTime(d: Date | null) {
    if (!d) return '';
    return `${fmtDate(d)}T${fmtTime(d)}`;
}

/* --------------------------------- UI --------------------------------- */
const DateTimePicker: React.FC<DateTimePickerProps> = ({
    value = '',
    onChange = () => { },
    label = 'Date & Time',
    pickerType = 'date-time',
    error,
    onBlur,
    minDate,
    maxDate,
    minTime,
    maxTime,
    required = false,
    disabled = false,
    size = 'medium',
    variant = 'default',
    helperText,
    id,
    name,
}) => {
    const selected =
        pickerType === 'date'
            ? parseDateOnly(value)
            : pickerType === 'time'
                ? parseTimeOnly(value)
                : parseDateTime(value);

    const sizes = {
        small: 'px-3 py-2 text-[13px] min-h-[36px]',
        medium: 'px-3.5 py-2.5 text-[15px] min-h-[42px]',
        large: 'px-4 py-3 text-base min-h-[48px]',
    };
    const variants = {
        default: cls(
            'border border-[var(--v2-border-glass-strong)]',
            error ? 'border-[var(--v2-danger)]/60 ring-0' : 'hover:border-[var(--v2-primary-400)]/50 focus:border-[var(--v2-primary-500)]',
            'focus:outline-none focus:ring-[3px] focus:ring-[var(--v2-primary-500)]/18'
        ),
        filled: cls(
            'border-0 bg-[var(--v2-bg-hover)]',
            error ? 'ring-0' : 'hover:bg-[var(--v2-bg-card)]',
            'focus:outline-none focus:ring-[3px] focus:ring-[var(--v2-primary-500)]/18'
        ),
        outlined: cls(
            'border-2 bg-transparent border-[var(--v2-border-glass-strong)]',
            error ? 'border-[var(--v2-danger)]/60' : 'hover:border-[var(--v2-primary-400)]/50 focus:border-[var(--v2-primary-500)]',
            'focus:outline-none focus:ring-[3px] focus:ring-[var(--v2-primary-500)]/18'
        ),
    } as const;
    const inputBase =
        'w-full rounded-[var(--v2-radius-md)] transition-all duration-[var(--v2-duration-normal)] ease-[var(--v2-ease-calm)] pr-10 ' +
        'disabled:opacity-60 disabled:cursor-not-allowed bg-[var(--v2-bg-surface)] text-[var(--v2-text-primary)] ' +
        'shadow-[0_1px_2px_rgba(0,0,0,0.04),inset_0_1px_0_rgba(255,255,255,0.015)] placeholder:text-[var(--v2-text-muted)] ';
    const inputClassName = cls(inputBase, sizes[size], variants[variant]);

    const minDateObj = parseDateOnly(minDate ?? '');
    const maxDateObj = parseDateOnly(maxDate ?? '');

    const isSameYMD = (a: Date | null, b: Date | null) =>
        !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

    /* -------------------- GUARANTEE BOTH OR NEITHER -------------------- */
    let computedMinTime: Date | undefined;
    let computedMaxTime: Date | undefined;

    if (pickerType === 'time') {
        // In time-only mode, only apply if BOTH provided
        if (minTime && maxTime) {
            computedMinTime = parseTimeOnly(minTime) || undefined;
            computedMaxTime = parseTimeOnly(maxTime) || undefined;
        }
    } else if (pickerType === 'date-time') {
        const appliesMin = isSameYMD(selected, minDateObj) && !!minTime;
        const appliesMax = isSameYMD(selected, maxDateObj) && !!maxTime;

        if (appliesMin && appliesMax) {
            // Same day has both bounds
            computedMinTime = parseTimeOnly(minTime);
            computedMaxTime = parseTimeOnly(maxTime);
        } else if (appliesMin) {
            // Only min bound applies for this day -> pair with end-of-day
            computedMinTime = parseTimeOnly(minTime);
            computedMaxTime = parseTimeOnly('23:59');
        } else if (appliesMax) {
            // Only max bound applies -> pair with start-of-day
            computedMinTime = parseTimeOnly('00:00');
            computedMaxTime = parseTimeOnly(maxTime);
        }
        // else: neither applies for this selected day -> leave both undefined
    }

    // If only one ended up defined for any reason, drop both (safety)
    if ((computedMinTime && !computedMaxTime) || (!computedMinTime && computedMaxTime)) {
        computedMinTime = undefined;
        computedMaxTime = undefined;
    }
    /* ------------------------------------------------------------------- */

    const handleChange = (d: Date | null) => {
        if (pickerType === 'date') return onChange(fmtDate(d));
        if (pickerType === 'time') return onChange(fmtTime(d));
        return onChange(fmtDateTime(d));
    };

    const inputId = id || name || `dtp-${Math.random().toString(36).slice(2, 9)}`;
    const icon = pickerType === 'time'
        ? <Icons.Clock className="h-5 w-5 text-[var(--v2-text-muted)]" />
        : <Icons.Calendar className="h-5 w-5 text-[var(--v2-text-muted)]" />;

    return (
        <div className="w-full">
            {label && (
                <label htmlFor={inputId} className="mb-2 block text-[15px] font-semibold tracking-[-0.005em] text-[var(--v2-text-primary)]">
                    {label}
                    {required && <span className="ml-1 text-[var(--v2-danger)]">*</span>}
                </label>
            )}

            <div className="relative text-[var(--v2-text-primary)]">
                <ReactDatePicker
                    id={inputId}
                    selected={selected}
                    onChange={handleChange}
                    onBlur={onBlur}
                    disabled={disabled}
                    showTimeSelect={pickerType === 'date-time'}
                    showTimeSelectOnly={pickerType === 'time'}
                    timeIntervals={5}
                    dateFormat={
                        pickerType === 'date' ? 'yyyy-MM-dd' :
                            pickerType === 'time' ? 'HH:mm' :
                                'yyyy-MM-dd HH:mm'
                    }
                    minDate={minDateObj || undefined}
                    maxDate={maxDateObj || undefined}
                    // pass time bounds only when both are defined
                    {...(computedMinTime && computedMaxTime
                        ? { minTime: computedMinTime, maxTime: computedMaxTime }
                        : {})}
                    className={inputClassName}
                    wrapperClassName="w-full"
                    popperClassName="z-[1000]"
                    placeholderText={
                        pickerType === 'date' ? 'YYYY-MM-DD' :
                            pickerType === 'time' ? 'HH:mm' :
                                'YYYY-MM-DD HH:mm'
                    }
                    customInputRef="input"
                    shouldCloseOnSelect={pickerType !== 'date-time'}
                />

                <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                    {icon}
                </div>
            </div>

            <div className="mt-1.5">
                {error ? (
                    <p className="text-[var(--v2-font-secondary)] font-medium text-[var(--v2-danger)]/80">{error}</p>
                ) : helperText ? (
                    <p className="text-[13px] text-[var(--v2-text-secondary)]">{helperText}</p>
                ) : null}
            </div>
        </div>
    );
};

export default DateTimePicker;
